import * as Sentry from '@sentry/react';
import { faro, getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';

let initialized = false;
let faroEnabled = false;
let releaseVersion = 'dev';
let navigationCaptured = false;
let environmentName = 'development';

function currentRoute(): string {
  return typeof window === 'undefined' ? '/' : `${window.location.pathname}${window.location.search}`;
}

function faroContext(properties: Record<string, unknown>) {
  return {
    release: releaseVersion,
    environment: environmentName,
    route: currentRoute(),
    ...properties,
  };
}

function captureFaroEvent(event: string, properties: Record<string, unknown>) {
  if (!faroEnabled || !faro.api) {
    return;
  }
  faro.api.pushEvent(event, faroContext(properties), 'sheshdash');
}

function collectNavigationRum(trigger: 'initial' | 'focus' | 'visibility') {
  if (typeof window === 'undefined' || navigationCaptured && trigger === 'initial') {
    return;
  }
  const nav = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  if (!nav) {
    return;
  }
  navigationCaptured = true;
  const fcpEntry = window.performance.getEntriesByName('first-contentful-paint')[0];
  captureFaroEvent('spa_route_perf', {
    trigger,
    response_end_ms: Math.round(nav.responseEnd),
    dom_content_loaded_ms: Math.round(nav.domContentLoadedEventEnd),
    load_event_ms: Math.round(nav.loadEventEnd),
    first_contentful_paint_ms: fcpEntry ? Math.round(fcpEntry.startTime) : null,
  });
}

export function initTelemetry() {
  if (initialized) {
    return;
  }
  initialized = true;

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const release = (import.meta.env.VITE_APP_VERSION as string | undefined)?.trim() || undefined;
  releaseVersion = release || 'dev';
  environmentName = ((import.meta.env.VITE_SENTRY_ENV as string | undefined) || 'development').trim() || 'development';
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: environmentName,
      release,
      tracesSampleRate: 0.1,
    });
  }

  const faroUrl = (import.meta.env.VITE_FARO_URL as string | undefined)?.trim();
  if (faroUrl) {
    initializeFaro({
      url: faroUrl,
      app: {
        name: 'sheshdash-spa',
        version: releaseVersion,
      },
      instrumentations: [
        ...getWebInstrumentations(),
      ],
      sessionTracking: {
        enabled: true,
      },
    });
    faroEnabled = true;
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('load', () => collectNavigationRum('initial'), { once: true });
    window.addEventListener('focus', () => collectNavigationRum('focus'));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        collectNavigationRum('visibility');
      }
    });
    window.setTimeout(() => collectNavigationRum('initial'), 3000);
  }
}

export function captureVersionMismatch(properties: {
  currentVersion: string;
  latestVersion: string;
  minimumSupportedVersion?: string | null;
  hardBlock: boolean;
}) {
  captureFaroEvent('spa_version_mismatch', properties);
}

/** Fired when the SPA bundle's `VITE_APP_VERSION` and the server's
 *  `/api/v1/version` come from incompatible namespaces — typically one
 *  is a commit SHA and the other is a release tag, indicating a CI
 *  image-tag collision (a non-tag pipeline overwrote the registry's
 *  `:SHA` reference that the prod-tag pipeline also wrote to). The
 *  banner is suppressed (refresh can't fix a build-baked version),
 *  but operators want visibility into how often this happens so they
 *  can drive the underlying CI fix. */
export function captureVersionShapeMismatch(properties: {
  currentVersion: string;
  serverVersion: string;
}) {
  captureFaroEvent('spa_version_shape_mismatch', properties);
}
